import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { runWhenPageReady } from '../utils/PageLifecycle';

gsap.registerPlugin(ScrollTrigger);

/**
 * SplitTextReveal - Animated character reveal on scroll
 * 
 * Features:
 * - Splits text into individual characters
 * - Creates mask wrapper for each character
 * - Animates characters from bottom to top with stagger
 * - H1s use a concise, editorial-style masked-letter reveal
 * - Fixed-duration timing keeps reveals consistent regardless of character count
 * - Optional parallax effect on parent container
 * - Scroll-linked animation (scrub)
 * 
 * Usage:
 * <h1 data-split-text-reveal data-parallax-amount="100">Your Text</h1>
 * 
 * Optional attributes:
 * - data-fixed-duration="1200" - Override default 1000ms animation duration
 * - data-parallax-amount="100" - Amount of parallax movement
 * - data-enable-scale="true" - Enable scale animation
 * - data-scale-amount="1.2" - Scale factor
 * - data-scrub="true" - Enable scroll-linked animation
 * - data-split-delay="0.5" - Delay before animation starts (seconds)
 */
class SplitTextReveal {
  constructor(element) {
    this.element = element;
    this.parallaxAmount = parseFloat(element.dataset.parallaxAmount) || 0;
    this.enableScale = element.dataset.enableScale === 'true';
    this.scaleAmount = parseFloat(element.dataset.scaleAmount) || 1.2;
    this.fixedDuration = parseFloat(element.dataset.fixedDuration) || 1000; // Fixed animation duration in ms
    this.scrub = element.dataset.scrub === 'true'; // Default to false (play once) unless specified
    this.animationDelay = parseFloat(element.dataset.splitDelay) || 0; // Delay before starting animation
    this.isMobile = window.innerWidth <= 768; // Detect mobile viewport
    this.isSafari = this.detectSafari(); // Detect Safari/iOS for italic rendering issues
    
    this.init();
  }
  
  /**
   * Detect Safari/iOS browsers
   * Safari has issues with italic character clipping even with overflow:visible
   */
  detectSafari() {
    const ua = navigator.userAgent.toLowerCase();
    // Detect Safari (not Chrome, not Firefox, not Edge)
    const isSafari = ua.indexOf('safari') !== -1 && ua.indexOf('chrome') === -1 && ua.indexOf('firefox') === -1;
    // Detect iOS devices
    const isIOS = /ipad|iphone|ipod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const detected = isSafari || isIOS;
    
    // Debug logging
    console.log('SplitTextReveal Safari Detection:', {
      userAgent: ua,
      isSafari,
      isIOS,
      detected,
      element: this.element
    });
    
    return detected;
  }
  
  /**
   * Split text into characters with nested mask structure
   * Preserves .last-word spans and <br> tags
   * On mobile: .last-word chars skip mask wrapper to avoid clipping italic swashes
   */
  splitText() {
    // Helper to create character mask (or plain span for .last-word on mobile)
    const createCharMask = (char, isLastWord = false) => {
      // On mobile, .last-word characters don't use mask wrapper
      // This prevents mobile browsers from clipping italic swashes
      if (this.isMobile && isLastWord) {
        const plainSpan = document.createElement('span');
        plainSpan.setAttribute('data-char-inner', '');
        plainSpan.setAttribute('data-char-nomask', ''); // Mark as unmasked
        plainSpan.style.display = 'inline-block';
        plainSpan.textContent = char;
        return plainSpan;
      }
      
      // Desktop: use mask wrapper (overflow: hidden)
      const outerSpan = document.createElement('span');
      outerSpan.setAttribute('data-char-mask', '');
      
      // Inner span - the animated character
      const innerSpan = document.createElement('span');
      innerSpan.setAttribute('data-char-inner', '');
      innerSpan.textContent = char;
      
      outerSpan.appendChild(innerSpan);
      return outerSpan;
    };

    // Process text node into character spans
    const processText = (text, isLastWord = false) => {
      const fragment = document.createDocumentFragment();
      
      if (isLastWord) {
        // If it's the last word, wrap all chars in a container
        const container = document.createElement('span');
        container.classList.add('last-word');
        
        const chars = text.split('');
        chars.forEach(char => {
          if (char === ' ') {
            container.appendChild(document.createTextNode(' '));
          } else if (char !== '\n' && char !== '\r') {
            container.appendChild(createCharMask(char, true)); // Pass true for isLastWord
          }
        });
        fragment.appendChild(container);
      } else {
        // Regular text: split by words to prevent breaking within words
        const parts = text.split(/(\s+)/);
        
        parts.forEach(part => {
          if (part.match(/^\s+$/)) {
            // Whitespace
            fragment.appendChild(document.createTextNode(part));
          } else if (part.length > 0) {
            // Word - wrap in inline-block span to prevent breaking
            const wordSpan = document.createElement('span');
            wordSpan.style.display = 'inline-block';
            wordSpan.style.whiteSpace = 'nowrap';
            
            const chars = part.split('');
            chars.forEach(char => {
              wordSpan.appendChild(createCharMask(char, false));
            });
            fragment.appendChild(wordSpan);
          }
        });
      }
      
      return fragment;
    };
    
    // Clone the element to work with
    const clone = this.element.cloneNode(true);
    const newContent = document.createDocumentFragment();
    
    // Process all child nodes (text, br, .last-word spans, etc.)
    const processChildNodes = (parent) => {
      const fragment = document.createDocumentFragment();
      
      Array.from(parent.childNodes).forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          // Text node - split into characters (trim to avoid extra spacing)
          const text = node.nodeValue;
          if (text.trim()) {
            fragment.appendChild(processText(text, false));
          }
        } else if (node.nodeName === 'BR') {
          // Preserve <br> tags with all attributes (like class="hide-mobile")
          const br = node.cloneNode(true);
          fragment.appendChild(br);
        } else if (node.classList && node.classList.contains('last-word')) {
          // Safari/iOS: Skip character splitting for .last-word to avoid italic clipping
          // Use whole-word fade animation instead
          if (this.isSafari) {
            console.log('Safari detected - creating word-fade for:', node.textContent);
            const lastWordSpan = document.createElement('span');
            lastWordSpan.classList.add('last-word');
            lastWordSpan.setAttribute('data-word-fade', ''); // Mark for whole-word fade
            lastWordSpan.textContent = node.textContent;
            fragment.appendChild(lastWordSpan);
          } else {
            console.log('Non-Safari - creating character split for:', node.textContent);
            // Other browsers: Split into characters as usual
            const lastWordSpan = document.createElement('span');
            lastWordSpan.classList.add('last-word');
            
            // Process the text content of the last word
            if (node.textContent) {
              const charsFragment = processText(node.textContent, true); // Pass true for isLastWord
              lastWordSpan.appendChild(charsFragment);
            }
            
            fragment.appendChild(lastWordSpan);
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          // Preserve element wrapper (e.g. <em>, <strong>)
          const wrapper = node.cloneNode(false);
          wrapper.appendChild(processChildNodes(node));
          fragment.appendChild(wrapper);
        }
      });
      
      return fragment;
    };
    
    newContent.appendChild(processChildNodes(clone));
    
    // Replace content
    this.element.innerHTML = '';
    this.element.appendChild(newContent);
    
    return this.element.querySelectorAll('[data-char-inner]');
  }
  
  /**
   * Initialize the animation
   */
  init() {
    // Keep headings immediately available to visitors who prefer reduced motion.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.element.classList.add('split-text-ready');
      return;
    }

    const chars = this.splitText();
    
    // Set parent element to visible (in case CSS hid it)
    gsap.set(this.element, {
      opacity: 1,
      transform: 'none' // Clear any CSS transforms
    });
    
    // Add class to make element visible now that it's been split
    this.element.classList.add('split-text-ready');
    
    // Safari/iOS: Handle whole-word fades for .last-word
    const wordFadeElements = this.element.querySelectorAll('[data-word-fade]');
    console.log('Found word-fade elements:', wordFadeElements.length, Array.from(wordFadeElements));
    if (wordFadeElements.length > 0) {
      this.setupWordFadeAnimation(wordFadeElements);
    }
    
    // Create array with proper stagger order: chars with their positions
    const charsWithOrder = Array.from(chars).map((char, index) => ({
      element: char,
      index: index,
      isLastWord: char.closest('.last-word') !== null
    }));
    
    // Separate regular chars from last-word chars for different animations
    const regularChars = charsWithOrder.filter(c => !c.isLastWord).map(c => c.element);
    const lastWordChars = charsWithOrder.filter(c => c.isLastWord);
    const isH1 = this.element.tagName.toLowerCase() === 'h1';
    // The reference animates every letter identically, including its final word.
    const revealChars = isH1 ? Array.from(chars) : regularChars;
    
    // H1s mirror the reference treatment: letters begin 200px below their
    // resting position rather than rising from a clipped character mask.
    if (revealChars.length > 0) {
      gsap.set(revealChars, {
        ...(isH1 ? { y: 200 } : { yPercent: 100 }),
        opacity: 0
      });
    }

    // Set initial state for last-word chars - just fade in place (no movement)
    if (!isH1 && lastWordChars.length > 0) {
      gsap.set(lastWordChars.map(c => c.element), {
        yPercent: 0,
        opacity: 0
      });
    }
    
    // Set initial scale if enabled (start large)
    if (this.enableScale) {
      gsap.set(this.element, {
        scale: this.scaleAmount
      });
    }
    
    // Set initial parallax offset if enabled
    if (this.parallaxAmount !== 0) {
      gsap.set(this.element, {
        y: this.parallaxAmount
      });
    }
    
    // Calculate a stagger that fits the *complete* reveal inside the requested
    // duration. Previously the character duration was added after the final
    // stagger, making longer H1s noticeably slower than shorter ones.
    const fixedDurationSeconds = this.fixedDuration / 1000;
    const totalChars = regularChars.length + lastWordChars.length;

    // Determine trigger point based on element type
    // h1 triggers immediately when visible, h2/h3 wait until further into viewport
    const triggerStart = isH1 ? 'top bottom' : 'top 85%';
    const characterDuration = isH1
      ? 1.5
      : fixedDurationSeconds * 0.8;
    // The reference headline has 147 letters at a 2ms stagger, producing an
    // approximately 300ms first-to-last cascade. Preserve that perceived
    // pacing on shorter headings instead of letting them animate all at once.
    const calculatedStagger = isH1
      ? (totalChars > 1 ? 0.3 / (totalChars - 1) : 0)
      : totalChars > 1
      ? Math.max(0, (fixedDurationSeconds - characterDuration) / (totalChars - 1))
      : 0;
    
    // Create a timeline for the animation
    const tl = gsap.timeline({
      delay: this.animationDelay, // Add delay before starting animation
      scrollTrigger: {
        trigger: this.element,
        start: triggerStart, // h1: immediate, h2/h3: when 85% down viewport
        end: 'center center',
        scrub: this.scrub ? 1 : false,
        toggleActions: this.scrub ? undefined : 'play none none none', // Play once, no reverse
        onLeave: () => {
          if (this.scrub) this.cleanupMasks();
        },
        onComplete: () => {
          if (!this.scrub) this.cleanupMasks();
        }
      }
    });

    // 1. Parent animation: parallax and scale down to normal
    if (this.parallaxAmount !== 0 || this.enableScale) {
      tl.to(this.element, {
        y: 0, // Animate TO zero (end at natural position)
        scale: 1, // Shrink to normal size
        duration: this.scrub ? 1 : 1.5,
        ease: this.scrub ? 'none' : 'power3.out'
      }, 0);
    }
    
    // 2. Staggered character reveal for regular characters
    // Uses calculated stagger to ensure fixed total duration regardless of character count
    if (revealChars.length > 0) {
      tl.to(revealChars, {
        ...(isH1 ? { y: 0 } : { yPercent: 0 }),
        opacity: 1,
        stagger: calculatedStagger,
        duration: this.scrub ? 1 : characterDuration,
        ease: isH1 ? 'expo.out' : 'power2.out'
      }, this.scrub ? 0.1 : 0); // Slight delay relative to container start
    }
    
    // 3. Animate last-word characters with "sunrise" effect
    // The whole .last-word rises from behind a mask instead of letter-by-letter fade
    if (!isH1 && lastWordChars.length > 0) {
      // Use "sun rising" animation for all .last-word elements
      // Pass the calculated stagger and total duration for proper timing
      this.setupSunRiseAnimation(tl, regularChars.length, calculatedStagger, fixedDurationSeconds);
    }
  }
  
  /**
   * Setup "sun rising" animation for all .last-word elements
   * The whole word rises from behind a mask instead of letter-by-letter
   * Calculates when regular character animation completes and triggers after
   */
  setupSunRiseAnimation(tl, regularCharsLength, calculatedStagger, fixedDurationSeconds) {
    const lastWordElements = this.element.querySelectorAll('.last-word');
    
    lastWordElements.forEach(word => {
      // Get the text content before it was split into chars
      const wordText = word.textContent;
      
      // Clear all the character mask divs and just put the text back
      word.innerHTML = wordText;
      
      // Set initial state - word starts below, will rise from behind h2's overflow:hidden
      gsap.set(word, {
        y: '50%',
        opacity: 0
      });
      
      // Calculate when the regular character animation completes
      // Last char starts at: (regularCharsLength - 1) * calculatedStagger
      // Start the sunrise slightly before the last char finishes for smooth overlap
      const lastCharStartTime = (regularCharsLength - 1) * calculatedStagger;
      const sunriseStartTime = lastCharStartTime + (fixedDurationSeconds * 0.3); // Start 30% into the last char's animation
      
      // Add to timeline - animate the word rising up from behind the horizon (h2)
      // Start as the last characters are animating in for smooth flow
      tl.to(word, {
        y: '0%',
        opacity: 1,
        duration: this.scrub ? 1 : fixedDurationSeconds * 0.8,
        ease: 'power3.out'
      }, this.scrub ? 0.1 : sunriseStartTime);
    });
  }
  
  /**
   * Setup whole-word fade animation for Safari/iOS .last-word elements
   * Avoids italic character clipping by not splitting into individual chars
   */
  setupWordFadeAnimation(wordElements) {
    console.log('setupWordFadeAnimation called with', wordElements.length, 'elements');
    wordElements.forEach((word, index) => {
      console.log(`Setting up word fade for element ${index}:`, word, word.textContent);
      // Set initial state
      gsap.set(word, {
        opacity: 0,
        y: 20
      });
      
      // Create fade-in animation
      ScrollTrigger.create({
        trigger: this.element,
        start: 'top 80%',
        once: true,
        onEnter: () => {
          console.log('Animating word:', word.textContent);
          gsap.to(word, {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: this.animationDelay + 0.3, // Sync with character animations
            ease: 'power2.out'
          });
        }
      });
    });
  }
  
  /**
   * Clean up mask structure and restore plain text
   * Preserves .last-word wrapper for italic styling
   * Handles both masked and unmasked characters
   */
  cleanupMasks() {
    // Unwrap all character masks
    const masks = this.element.querySelectorAll('[data-char-mask]');
    masks.forEach(mask => {
      const char = mask.querySelector('[data-char-inner]');
      if (char) {
        const textNode = document.createTextNode(char.textContent);
        mask.parentNode.insertBefore(textNode, mask);
        mask.remove();
      }
    });
    
    // Also unwrap unmasked characters (mobile .last-word chars)
    const unmasked = this.element.querySelectorAll('[data-char-nomask]');
    unmasked.forEach(char => {
      const textNode = document.createTextNode(char.textContent);
      char.parentNode.insertBefore(textNode, char);
      char.remove();
    });
    
    // Keep .last-word wrapper intact - it's needed for italic styling
  }
}

/**
 * Auto-initialize all elements with data-split-text-reveal
 * Excludes carousel slides which handle their own initialization
 */
function initSplitTextReveal() {
  const elements = document.querySelectorAll('[data-split-text-reveal]');
  elements.forEach(element => {
    // Skip elements inside carousel containers (they handle their own init)
    const isInsideCarousel = element.closest('.offers-carousel, .room-carousel, [data-carousel]');
    if (!isInsideCarousel) {
      new SplitTextReveal(element);
    }
  });
}

runWhenPageReady(initSplitTextReveal);

// Refresh ScrollTrigger when window fully loads (images, fonts, etc.)
window.addEventListener('load', () => {
  ScrollTrigger.refresh();
});

export default SplitTextReveal;
